import { useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { hapticTap } from "@/lib/native-haptics";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { getPreviousPortalPath } from "@/hooks/usePortalNavHistory";

/** Root tabs that should NOT show a back header */
const ROOT_TABS = new Set(["dashboard", "messages", "me", "dial", "history", "contacts", "topup", "rates"]);

const ROUTE_MAP: Record<string, { parent: string; titleKey: string; parentLabelKey: string }> = {
  "topup":          { parent: "/connect-portal/dashboard",  titleKey: "topUpNav",        parentLabelKey: "home" },
  "topup/success":  { parent: "/connect-portal/dashboard",  titleKey: "payment",         parentLabelKey: "home" },
  
  "referrals":      { parent: "/connect-portal/me",         titleKey: "referrals",       parentLabelKey: "account" },
  "profile":        { parent: "/connect-portal/me",         titleKey: "profile",         parentLabelKey: "account" },
  "help":           { parent: "/connect-portal/me",         titleKey: "help",            parentLabelKey: "account" },
  "privacy":        { parent: "/connect-portal/me",         titleKey: "privacy",         parentLabelKey: "account" },
  "terms":          { parent: "/connect-portal/me",         titleKey: "terms",           parentLabelKey: "account" },
  "about":          { parent: "/connect-portal/me",         titleKey: "about",           parentLabelKey: "account" },
  "how-it-works":   { parent: "/connect-portal/dashboard",  titleKey: "howItWorks",      parentLabelKey: "home" },
  "billing-info":   { parent: "/connect-portal/me",         titleKey: "billing",         parentLabelKey: "account" },
  "radio":          { parent: "/connect-portal/me",         titleKey: "radio",           parentLabelKey: "account" },
  "scan":           { parent: "/connect-portal/me",         titleKey: "scanQr",          parentLabelKey: "account" },
  "my-qr":          { parent: "/connect-portal/me",         titleKey: "myQrCodeNav",     parentLabelKey: "account" },
  "add":            { parent: "/connect-portal/messages",   titleKey: "addContact",      parentLabelKey: "chats" },
  "free-radio":     { parent: "/connect-portal/dashboard",  titleKey: "freeRadio",       parentLabelKey: "home" },
  "notifications":              { parent: "/connect-portal/me", titleKey: "notifications",            parentLabelKey: "account" },
  "notifications/preferences":  { parent: "/connect-portal/notifications", titleKey: "notificationPreferences", parentLabelKey: "notifications" },
};

/** Capitalize a slug like "south-sudan" → "South Sudan" */
function slugToDisplayName(slug: string): string {
  return slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function getRouteKey(pathname: string) {
  const prefix = "/connect-portal/";
  if (!pathname.startsWith(prefix)) return null;
  const subPath = pathname.slice(prefix.length).replace(/\/$/, "");
  if (!subPath || ROOT_TABS.has(subPath)) return null;
  if (ROUTE_MAP[subPath]) return ROUTE_MAP[subPath];

  // Dynamic fallback: any unknown single-segment subpath → treat as country page → back to Rates
  if (!subPath.includes("/")) {
    return {
      parent: "/connect-portal/rates",
      titleKey: slugToDisplayName(subPath),
      parentLabelKey: "rates",
    };
  }

  return null;
}

interface ConnectBackHeaderProps {
  sentinelRef?: React.RefObject<HTMLDivElement>;
}

export function ConnectBackHeader({ sentinelRef }: ConnectBackHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const headerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("common");

  const routeInfo = getRouteKey(location.pathname);

  const title = useMemo(() => routeInfo ? t(routeInfo.titleKey) : "", [routeInfo, t]);
  const parentLabel = useMemo(() => routeInfo ? t(routeInfo.parentLabelKey) : "", [routeInfo, t]);

  useEffect(() => {
    if (!sentinelRef?.current || !headerRef.current) return;
    const header = headerRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          header.classList.remove("shadow-sm", "border-b", "border-border");
        } else {
          header.classList.add("shadow-sm", "border-b", "border-border");
        }
      },
      { threshold: 0 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [sentinelRef]);

  const handleBack = useCallback(() => {
    hapticTap();
    if (!routeInfo) return;
    // For notification routes, ALWAYS use the mapped parent - using history
    // here creates a Notifications ↔ Preferences loop when the user enters
    // Preferences from the Notifications gear icon.
    const isNotifRoute = location.pathname.startsWith("/connect-portal/notifications");
    if (isNotifRoute) {
      navigate(routeInfo.parent);
      return;
    }
    // Prefer the actual previous in-portal page (e.g. Top-up reached from Account
    // returns to Account, not Dashboard). Fall back to the mapped parent route.
    const prev = getPreviousPortalPath(location.pathname);
    navigate(prev || routeInfo.parent);
  }, [navigate, routeInfo, location.pathname]);

  if (!routeInfo) return null;

  return (
    <div
      ref={headerRef}
      className="sticky top-0 z-45 bg-background stable-sticky transition-[box-shadow,border-color] duration-200"
      style={{
        paddingTop: "env(safe-area-inset-top, 0px)",
        isolation: "isolate",
        backgroundColor: "hsl(var(--background))",
      }}
    >
      <div className="flex items-center gap-1 h-11 px-2">
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={handleBack}
          className="flex items-center gap-0.5 h-11 min-w-[44px] px-2 rounded-full text-primary hover:bg-accent/50 transition-colors active:bg-accent"
          aria-label={`${t("back")} ${parentLabel}`}
        >
          <ArrowLeft className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm font-medium truncate">{parentLabel}</span>
        </motion.button>
        <h1 className="text-base font-semibold text-foreground ml-1 truncate">
          {title}
        </h1>
      </div>
    </div>
  );
}

/** Export the route lookup for Android back handler */
export function getRouteInfo(pathname: string) {
  const info = getRouteKey(pathname);
  if (!info) return null;
  // Return compatible shape for Android back handler
  return { parent: info.parent, title: info.titleKey, parentLabel: info.parentLabelKey };
}
